/* eslint-disable react/no-did-mount-set-state -- Special case - unavoidable */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import update from 'update-immutable';
import { throttle, isNil, isEmpty } from 'lodash';
import cc from 'classcat';
import { isChrome } from 'react-device-detect';
import Bourne from '@hapi/bourne';
import screenfull from 'screenfull';
import loadable from '@loadable/component';
import * as Sentry from '@sentry/react';
import checkMobile from 'lib/isMobile';
import { isLoading } from 'lib/redux-utils';
import IOSversion from 'lib/iOSversion';
import Api from 'services/Api';
import SlotModule, { actionTypes as SlotTypes } from 'modules/Slot';
import Wallet from 'modules/Wallet';
import Modals from 'modules/Modals';
import User from 'modules/User';
import Lobby from 'modules/Lobby';
import GameLoadingScreen from 'components/GameLoadingScreen';

const SlotDeposit = loadable(() => import('./SlotDeposit'));

const RESIZE_MS = 100;
const KEEP_ALIVE_INTERVAL = 5 * 60 * 1000;
const LOADING_SCREEN_TIMEOUT = 8 * 1000;
const DISCARD_MESSAGES =
  process.env.REACT_DEV &&
  new Set(['react-devtools-content-script', 'react-devtools-bridge', '@devtools-page']);

const isMobile = checkMobile();
/* https://mrq.atlassian.net/browse/MRQ-502 */
const isBuggy = isChrome && IOSversion().version === 16;

export class _SlotFrame extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFullscreen: false,
      shouldRemoveLoadingScreen: false
    };
  }

  componentDidMount() {
    const { initialize, kycStatus, frameResized, sendKeepAlive, searchRef } = this.props;
    initialize({ searchRef });
    if (this.ifr) {
      this.ifr.onLoad = () => {
        window.addEventListener('outgoingFrameMessage', this.handleOutgoingFrameMessage, {
          passive: true
        });
      };
    }
    window.addEventListener('message', this.handleIncomingFrameMessage, { passive: true });
    // Wait 3 seconds for the session. If it doesn't come back, launch demo mode.
    // Start the resize listener
    if (!isNil(frameResized)) {
      this.props.initSize(this.frameWrap);
      window.addEventListener('resize', this.resizer, { passive: true });
      window.addEventListener('orientationchange', this.orientationChange, { passive: true });
    }

    this.keepAliveInterval = setInterval(() => {
      if (kycStatus === User.constants.kyc.PASS) sendKeepAlive();
    }, KEEP_ALIVE_INTERVAL);

    this.loadingScreenTimeout = setTimeout(() => {
      this.setState({
        shouldRemoveLoadingScreen: true
      });
    }, LOADING_SCREEN_TIMEOUT);

    if (screenfull.isEnabled) {
      screenfull.on('change', this.handleFullscreenChange);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      balance,
      lastTransactionType,
      loading,
      isRealityCheckOpen,
      paymentMethodRefs,
      provider,
      stopAutoplay,
      startAutoplay,
      orientation,
      initSize
    } = this.props;

    if (prevProps.loading && !loading) {
      window.addEventListener('outgoingFrameMessage', this.handleOutgoingFrameMessage, {
        passive: true
      });
    }

    if (prevProps.balance !== balance && !lastTransactionType.startsWith('SLOT_')) {
      const event = new CustomEvent('outgoingFrameMessage', {
        detail: {
          eventid: 'changebalance',
          data: { balance: parseFloat(balance) }
        }
      });
      window.dispatchEvent(event);
    }

    if (prevProps.paymentMethodRefs !== paymentMethodRefs) {
      initSize(this.frameWrap);
    }

    if (prevProps.orientation !== orientation) {
      initSize(this.frameWrap);
    }

    if (!prevProps.isRealityCheckOpen && isRealityCheckOpen) {
      stopAutoplay(provider);
    } else if (prevProps.isRealityCheckOpen && !isRealityCheckOpen) {
      startAutoplay(provider);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleIncomingFrameMessage, { passive: true });
    window.removeEventListener('outgoingFrameMessage', this.handleOutgoingFrameMessage, {
      passive: true
    });
    window.removeEventListener('resize', this.resizer, { passive: true });
    window.removeEventListener('orientationchange', this.orientationChange, { passive: true });
    clearInterval(this.keepAliveInterval);
    clearTimeout(this.loadingScreenTimeout);
    this.resizer.cancel();
    this.orientationChange.cancel();
    if (screenfull.isEnabled) {
      screenfull.off('change', this.handleFullscreenChange);
    }
  }

  setIfr = (_) => {
    this.ifr = _;
  };

  setFrameWrap = (_) => {
    this.frameWrap = _;
  };

  get meta() {
    const { platform, provider, refName } = this.props;
    return {
      platform,
      provider,
      refName
    };
  }

  handleOutgoingFrameMessage = (e) => {
    // Already stringified
    const { provider } = this.props;
    if (e.detail.meta?.provider === provider) {
      const detail = update(e.detail, { $unset: 'meta' });
      this.ifr.contentWindow.postMessage(detail, '*');
    }
  };

  handleIncomingFrameMessage = (e) => {
    const { data, origin } = e;
    const { platform } = this.props;

    if (process.env.REACT_DEV && DISCARD_MESSAGES.has(data?.source)) return;

    // Sometimes data is null, I think it happens when opening a game that is not enabled in lobby.
    // But just in case if/when that happens the page crashes, and we want to prevent that.
    if (!data) return;

    if (origin === window.location.origin && data.meta?.source === 'MRQ_IFRAME') {
      /*
      If a game iframe redirects itself to the domain that the app runs in (ex: mrq.com)
      most likely due to miss-configuration,
      we're posting a frame message there (see index.html) which we're intercepting here.
      There we're sending a full navigation action representing the exact URL that the frame redirected itself
      which for the moment we ignore here, and we're simply redirecting to lobby page.
      */
      this.props.dispatchFrameEvent(SlotModule.actions.closeGame({ meta: this.meta }));
      Sentry.captureMessage('Fallback game iframe redirection', {
        level: 'info',
        contexts: {
          game: this.meta
        }
      });
    }

    // Always verify the sender's identity using the origin and possibly source properties
    if (SlotModule.constants.PLATFORMS.includes(platform)) {
      Bourne.scan(data, { protoAction: 'remove' });

      this.props.dispatchFrameEvent({
        type: 'FrameEvent/INCOMING',
        payload: data,
        meta: {
          ...this.meta,
          incoming: true
        }
      });
    }
  };

  // eslint-disable-next-line react/sort-comp -- the position is fine
  resizer = throttle(
    () => {
      this.props.frameResized(this.frameWrap);
    },
    RESIZE_MS,
    { leading: false }
  );

  frameOrientationChanged = () => {
    this.props.frameOrientationChanged(this.frameWrap);
  };

  orientationChange = throttle(this.frameOrientationChanged, RESIZE_MS, { leading: false });

  handleFullscreenChange = () => {
    this.setState({
      isFullscreen: screenfull.isFullscreen
    });
  };

  handleFullscreenToggle = () => {
    if (screenfull.isEnabled) {
      screenfull.toggle(this.frameWrap);
    }
  };

  render() {
    const {
      className,
      name,
      mobileUrl,
      desktopUrl,
      placeholder,
      paymentMethodRefs,
      paymentMethods,
      loading,
      hideSlotDeposit,
      isGameReady,
      refName
    } = this.props;
    const { isFullscreen, shouldRemoveLoadingScreen } = this.state;
    const url = isMobile ? mobileUrl : desktopUrl;
    const showDeposit = !isEmpty(paymentMethodRefs) && !hideSlotDeposit;
    const isLoadingScreenActive = !isGameReady && !shouldRemoveLoadingScreen;
    const frameWrapClass = cc([
      'frame-wrap',
      showDeposit && 'frame-wrap--deposit',
      isFullscreen && 'frame-wrap--isFullscreen',
      !isLoadingScreenActive && 'frame-wrap--gameReady'
    ]);
    return (
      <div className={frameWrapClass} ref={this.setFrameWrap}>
        <div className="frame-wrap__container">
          <GameLoadingScreen
            logoUrl={placeholder}
            isActive={isLoadingScreenActive}
            gameRef={refName}
          />
          {url && !loading && (
            <iframe
              title={name}
              ref={this.setIfr}
              seamless
              scrolling="no"
              className={`slot-frame frame ${className}`}
              src={url}
            />
          )}
        </div>
        {showDeposit && !loading && screenfull.isEnabled && !isBuggy ? (
          <SlotDeposit
            paymentMethodRefs={paymentMethodRefs}
            paymentMethods={paymentMethods}
            isFullscreen={isFullscreen}
            handleFullscreenToggle={this.handleFullscreenToggle}
          />
        ) : null}
      </div>
    );
  }
}

_SlotFrame.propTypes = {
  initialize: PropTypes.func.isRequired,
  className: PropTypes.string,
  mobileUrl: PropTypes.string,
  desktopUrl: PropTypes.string,
  lastTransactionType: PropTypes.string,
  name: PropTypes.string,
  isGameReady: PropTypes.bool.isRequired,
  hideSlotDeposit: PropTypes.bool.isRequired,
  // ESLint can't see the prop usage in mapDispatchToProps
  // eslint-disable-next-line react/no-unused-prop-types -- eslint false positive
  refName: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  kycStatus: PropTypes.oneOf(Object.values(User.constants.kyc)).isRequired,
  balance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  platform: PropTypes.string,
  provider: PropTypes.string,
  frameResized: PropTypes.func,
  initSize: PropTypes.func,
  placeholder: PropTypes.string,
  paymentMethods: PropTypes.object,
  paymentMethodRefs: PropTypes.arrayOf(PropTypes.string)
};

_SlotFrame.defaultProps = {
  className: '',
  mobileUrl: null,
  desktopUrl: null,
  name: '',
  lastTransactionType: null,
  platform: null,
  provider: null,
  frameResized: null,
  initSize: null,
  paymentMethods: null,
  paymentMethodRefs: null,
  loading: true,
  placeholder: ''
};

const mapStateToProps = (state, { refName }) => {
  const slot = SlotModule.selectors.getRoom(state, refName);
  return {
    searchRef: Lobby.selectors.getSearchResult(state)?.searchRef,
    mobileUrl: slot && slot.mobileUrl,
    desktopUrl: slot && slot.desktopUrl,
    platform: slot && slot.platform,
    provider: slot && slot.provider,
    orientation: slot && slot.orientation,
    hideSlotDeposit: !!slot?.hideSlotDeposit,
    isGameReady: !!slot?.gameReady,
    balance: Wallet.selectors.getBalanceReal(state),
    kycStatus: User.selectors.getKycStatus(state),
    lastTransactionType: Wallet.selectors.model(state).lastTransactionType,
    loading: isLoading(state, SlotTypes.AT.ROOM._),
    isRealityCheckOpen: Modals.selectors.getModal(state, 'realityCheck').visible,
    name: state.Slot.name
  };
};

const mapDispatchToProps = (dispatch, { refName }) => ({
  initialize: ({ searchRef }) => {
    Api.actions.slot.room({ refName }, { searchRef })(dispatch);
  },
  sendKeepAlive: async () => {
    try {
      return await Api.actions.slot.keepAlive({ refName })(dispatch);
    } catch (e) {
      dispatch(Modals.actions.open('keepAliveSlotError'));
      return e;
    }
  },
  stopAutoplay: (provider) => dispatch(SlotModule.actions.stopAutoplay(provider)),
  startAutoplay: (provider) => dispatch(SlotModule.actions.startAutoplay(provider)),
  dispatchFrameEvent: (e) => dispatch(e)
});

export default connect(mapStateToProps, mapDispatchToProps)(_SlotFrame);
